import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { DirectUploadProvider } from '@docflow/react-activestorage-provider';
import { Loader2, Mountain, Trash2, Upload } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { MarkdownEditor } from '@/components/ui/markdown-editor';
import { Skeleton } from '@/components/ui/skeleton';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useToast } from '@/components/ui/use-toast';
import { cragsService, type CragGradingSystems } from '../../services/crags';
import LocationHeader from '../../components/location/LocationHeader';
import { GymInfoFields, type GymInfoValues } from '../../components/gym/GymInfoFields';
import { CountryStateFields } from '../../components/location/CountryStateFields';
import { useCurrentCrag } from '../hooks/useCurrentCrag';
import { apiClient, tokenStorage } from '../../utils/api-client';
import type { GalleryImage } from '../../types/location-images';

/** Radix Select treats '' as "no value", so "use the default" carries a token. */
const DEFAULT_SYSTEM = '__default__';

/**
 * Where the direct upload posts its blobs. Same shape as everywhere else the
 * app uploads: the API's origin when it is configured, the page's own when the
 * two are served together.
 */
function apiOrigin() {
  const baseURL = apiClient.getBaseURL();
  const fallback = {
    host: window.location.hostname,
    port: window.location.port || undefined,
    protocol: window.location.protocol.replace(':', ''),
  };
  if (!baseURL) return fallback;
  try {
    const url = new URL(baseURL);
    return { host: url.hostname, port: url.port || undefined, protocol: url.protocol.replace(':', '') };
  } catch {
    return fallback;
  }
}

const EMPTY_SYSTEMS: CragGradingSystems = { boulder: [], route: [] };

interface CragInfoValues extends GymInfoValues {
  country: string;
  state: string;
  boulderGradingSystemId: string;
  routeGradingSystemId: string;
  descriptionEn: string;
  descriptionIt: string;
  parkingLatitude: string;
  parkingLongitude: string;
}

const EMPTY_INFO: CragInfoValues = {
  websiteUrl: '',
  address: '',
  city: '',
  zipcode: '',
  latitude: '',
  longitude: '',
  country: '',
  state: '',
  boulderGradingSystemId: '',
  routeGradingSystemId: '',
  descriptionEn: '',
  descriptionIt: '',
  parkingLatitude: '',
  parkingLongitude: '',
};

interface CoordinatePairProps {
  title: string;
  /** Prefixes the input ids, so the two pairs on the page stay distinguishable. */
  idPrefix: string;
  latitude: string;
  longitude: string;
  onChange: (patch: { latitude?: string; longitude?: string }) => void;
}

/**
 * A latitude/longitude pair under its own heading — the crag's, or the
 * parking's. Both are edited in the same block so it is plain there are two of
 * them, and which is which.
 */
function CoordinatePair({ title, idPrefix, latitude, longitude, onChange }: CoordinatePairProps) {
  const { t } = useTranslation();

  return (
    <fieldset className="space-y-3 rounded-lg border p-3">
      <legend className="px-1 text-sm font-medium">{title}</legend>
      <div className="space-y-2">
        <Label htmlFor={`${idPrefix}Latitude`} className="text-xs text-muted-foreground">
          {t('gymSettings.latitude')}
        </Label>
        <Input
          id={`${idPrefix}Latitude`}
          type="number"
          step="any"
          min="-90"
          max="90"
          value={latitude}
          onChange={(e) => onChange({ latitude: e.target.value })}
        />
      </div>
      <div className="space-y-2">
        <Label htmlFor={`${idPrefix}Longitude`} className="text-xs text-muted-foreground">
          {t('gymSettings.longitude')}
        </Label>
        <Input
          id={`${idPrefix}Longitude`}
          type="number"
          step="any"
          min="-180"
          max="180"
          value={longitude}
          onChange={(e) => onChange({ longitude: e.target.value })}
        />
      </div>
    </fieldset>
  );
}

/**
 * Admin-only crag profile — the gym Info page's counterpart for a piece of rock.
 * Logo, cover, gallery and description go through LocationHeader (which handles
 * the ActiveStorage uploads and works off the location id, so it needs nothing
 * crag-specific); the rest is saved in one PATCH.
 *
 * Two fields a gym's page has no use for: the **country/state** pair, since a
 * crag is somewhere rather than at an address, and the **grading systems**,
 * because outdoor grades are the crag's own convention rather than something
 * chosen per climb.
 */
export default function CragInfoPage() {
  const { t } = useTranslation();
  const { toast } = useToast();
  const { crag, loading, setCrag, slug } = useCurrentCrag();

  const [info, setInfo] = useState<CragInfoValues>(EMPTY_INFO);
  const [systems, setSystems] = useState<CragGradingSystems>(EMPTY_SYSTEMS);
  const [saving, setSaving] = useState(false);

  // The approach track is uploaded straight to storage, then saved by signed id
  // with the rest of the form — so picking a file and then abandoning the page
  // leaves the crag as it was.
  const [gpxSignedId, setGpxSignedId] = useState<string | null>(null);
  const [gpxName, setGpxName] = useState<string | null>(null);
  const [gpxUploading, setGpxUploading] = useState(false);
  const [removeGpx, setRemoveGpx] = useState(false);
  const gpxInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!crag) return;
    setInfo({
      websiteUrl: crag.website_url || '',
      address: crag.address || '',
      city: crag.city || '',
      zipcode: crag.zipcode || '',
      latitude: crag.latitude != null ? String(crag.latitude) : '',
      longitude: crag.longitude != null ? String(crag.longitude) : '',
      country: crag.country || '',
      state: crag.state || '',
      boulderGradingSystemId: crag.boulder_grading_system_id || '',
      routeGradingSystemId: crag.route_grading_system_id || '',
      descriptionEn: crag.description_en || '',
      descriptionIt: crag.description_it || '',
      parkingLatitude: crag.parking_latitude != null ? String(crag.parking_latitude) : '',
      parkingLongitude: crag.parking_longitude != null ? String(crag.parking_longitude) : '',
    });
  }, [crag]);

  // The pickers' catalogue. A crag with no custom systems still gets the global
  // ones, so this is never empty in practice — but a failure here must not stop
  // the rest of the form saving, hence the silent fallback.
  useEffect(() => {
    if (!slug) return;
    let active = true;
    cragsService
      .getGradingSystems(slug)
      .then((data) => { if (active) setSystems(data); })
      .catch(() => { if (active) setSystems(EMPTY_SYSTEMS); });
    return () => { active = false; };
  }, [slug]);

  const handleSave = async () => {
    if (!slug) return;
    setSaving(true);
    try {
      const updated = await cragsService.updateCrag(slug, {
        website_url: info.websiteUrl,
        address: info.address,
        city: info.city,
        zipcode: info.zipcode,
        // Country is required on a Location, so a blank one is the form not
        // having loaded rather than the admin clearing it — never send it.
        ...(info.country ? { country: info.country } : {}),
        state: info.state,
        latitude: info.latitude ? parseFloat(info.latitude) : null,
        longitude: info.longitude ? parseFloat(info.longitude) : null,
        boulder_grading_system_id: info.boulderGradingSystemId || null,
        route_grading_system_id: info.routeGradingSystemId || null,
        description_en: info.descriptionEn,
        description_it: info.descriptionIt,
        parking_latitude: info.parkingLatitude ? parseFloat(info.parkingLatitude) : null,
        parking_longitude: info.parkingLongitude ? parseFloat(info.parkingLongitude) : null,
        // The track only travels when a new one has just been picked, or when
        // the one that is there has been dropped.
        ...(gpxSignedId ? { approach_gpx_signed_id: gpxSignedId } : {}),
        ...(removeGpx && !gpxSignedId ? { remove_approach_gpx: true } : {}),
      });
      setCrag(updated);
      setGpxSignedId(null);
      setGpxName(null);
      setRemoveGpx(false);
      toast({ title: t('gymAdmin.saved') });
    } catch (error) {
      toast({
        title: t('gymAdmin.saveError'),
        description: error instanceof Error ? error.message : t('errors.unexpectedError'),
        variant: 'destructive',
      });
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <Skeleton className="h-96 w-full max-w-4xl" />;
  if (!crag) return null;

  // What the approach row shows: the file just picked, else the one already on
  // the crag — unless it has been marked for removal and not yet saved.
  const currentGpxName = gpxName ?? (removeGpx ? null : crag.approach_gpx_filename ?? null);

  const gradingSelect = (
    id: string,
    labelKey: string,
    value: string,
    options: CragGradingSystems['boulder'],
    onChange: (id: string) => void
  ) => (
    <div className="space-y-2">
      <Label htmlFor={id}>{t(labelKey)}</Label>
      <Select
        value={value || DEFAULT_SYSTEM}
        onValueChange={(next) => onChange(next === DEFAULT_SYSTEM ? '' : next)}
      >
        <SelectTrigger id={id}>
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value={DEFAULT_SYSTEM}>{t('cragAdmin.info.defaultGradingSystem')}</SelectItem>
          {options.map((system) => (
            <SelectItem key={system.id} value={system.id}>
              {system.name}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );

  return (
    <div className="max-w-4xl space-y-6">
      {/* Logo, cover, gallery and short description (bio) */}
      <Card>
        <CardHeader>
          <CardTitle>{t('gymAdmin.info.imagesTitle')}</CardTitle>
        </CardHeader>
        <CardContent>
          <LocationHeader
            locationId={crag.id}
            name={crag.name}
            bio={crag.bio}
            coverImageUrl={crag.cover_image_url}
            logoUrl={crag.logo_url}
            logoSmallUrl={crag.logo_small_url}
            galleryImages={crag.gallery_images ?? []}
            fallbackIcon={<Mountain className="w-8 h-8 text-muted-foreground" />}
            isStaffMember
            isStaffAdmin
            hideName
            onBioUpdated={(bio) => setCrag((c) => (c ? { ...c, bio } : c))}
            onCoverUpdated={(url) => setCrag((c) => (c ? { ...c, cover_image_url: url } : c))}
            onLogoUpdated={(url, smallUrl) =>
              setCrag((c) => (c ? { ...c, logo_url: url, logo_small_url: smallUrl } : c))
            }
            onGalleryUpdated={(imgs: GalleryImage[]) =>
              setCrag((c) => (c ? { ...c, gallery_images: imgs } : c))
            }
          />
        </CardContent>
      </Card>

      {/* Where it is, how to reach it, and how it grades */}
      <Card>
        <CardHeader>
          <CardTitle>{t('gymAdmin.info.detailsTitle')}</CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* The coordinates are edited in the guide card below, beside the
              parking's — on a crag the two are read together. */}
          <GymInfoFields
            values={info}
            onChange={(patch) => setInfo((prev) => ({ ...prev, ...patch }))}
            hideCoordinates
          />
          <CountryStateFields
            country={info.country}
            state={info.state}
            onChange={(patch) => setInfo((prev) => ({ ...prev, ...patch }))}
          />
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {gradingSelect(
              'boulderGradingSystem',
              'cragAdmin.info.boulderGradingSystem',
              info.boulderGradingSystemId,
              systems.boulder,
              (id) => setInfo((prev) => ({ ...prev, boulderGradingSystemId: id }))
            )}
            {gradingSelect(
              'routeGradingSystem',
              'cragAdmin.info.routeGradingSystem',
              info.routeGradingSystemId,
              systems.route,
              (id) => setInfo((prev) => ({ ...prev, routeGradingSystemId: id }))
            )}
          </div>
          <Button onClick={handleSave} disabled={saving}>
            {saving ? t('gymAdmin.saving') : t('gymAdmin.save')}
          </Button>
        </CardContent>
      </Card>

      {/* What climbers read on the crag's Info tab: the write-up, which way it
          faces, where to park and how to walk in. Saved by the same button as
          the block above — it is one form, split into two cards. */}
      <Card>
        <CardHeader>
          <CardTitle>{t('cragAdmin.info.guideTitle')}</CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="space-y-2">
            <Label>{t('cragAdmin.info.description')}</Label>
            <p className="text-sm text-muted-foreground">{t('cragAdmin.info.descriptionHint')}</p>
            {/* Both languages, one at a time: the same person writes them, and
                a reader only ever sees theirs. */}
            <Tabs defaultValue="it">
              <TabsList>
                <TabsTrigger value="it">{t('cragAdmin.info.italian')}</TabsTrigger>
                <TabsTrigger value="en">{t('cragAdmin.info.english')}</TabsTrigger>
              </TabsList>
              <TabsContent value="it" className="mt-2">
                <MarkdownEditor
                  id="descriptionIt"
                  value={info.descriptionIt}
                  onChange={(value) => setInfo((prev) => ({ ...prev, descriptionIt: value }))}
                  placeholder={t('cragAdmin.info.descriptionPlaceholder')}
                />
              </TabsContent>
              <TabsContent value="en" className="mt-2">
                <MarkdownEditor
                  id="descriptionEn"
                  value={info.descriptionEn}
                  onChange={(value) => setInfo((prev) => ({ ...prev, descriptionEn: value }))}
                  placeholder={t('cragAdmin.info.descriptionPlaceholder')}
                />
              </TabsContent>
            </Tabs>
          </div>

          {/* Both pairs side by side, because they are read against each other:
              one is the rock, the other is where the car stops, and on a crag
              they are rarely the same spot. Each gets its own map on the
              member page; only the parking gets a navigate button. */}
          <div className="space-y-2">
            <Label>{t('cragAdmin.info.coordinates')}</Label>
            <p className="text-sm text-muted-foreground">{t('cragAdmin.info.coordinatesHint')}</p>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <CoordinatePair
                title={t('cragAdmin.info.cragCoordinates')}
                idPrefix="crag"
                latitude={info.latitude}
                longitude={info.longitude}
                onChange={(patch) => setInfo((prev) => ({ ...prev, ...patch }))}
              />
              <CoordinatePair
                title={t('cragAdmin.info.parkingCoordinates')}
                idPrefix="parking"
                latitude={info.parkingLatitude}
                longitude={info.parkingLongitude}
                onChange={(patch) => setInfo((prev) => ({
                  ...prev,
                  ...(patch.latitude !== undefined ? { parkingLatitude: patch.latitude } : {}),
                  ...(patch.longitude !== undefined ? { parkingLongitude: patch.longitude } : {}),
                }))}
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label>{t('cragAdmin.info.approach')}</Label>
            <p className="text-sm text-muted-foreground">{t('cragAdmin.info.approachHint')}</p>
            <div className="flex flex-wrap items-center gap-3">
              {currentGpxName && (
                <span className="text-sm">{currentGpxName}</span>
              )}
              <DirectUploadProvider
                origin={apiOrigin()}
                headers={{ Authorization: `Bearer ${tokenStorage.getToken() || ''}` }}
                onSuccess={(signedIds: string[]) => {
                  setGpxUploading(false);
                  if (!signedIds || signedIds.length === 0) {
                    setGpxName(null);
                    toast({ title: t('cragAdmin.info.gpxUploadError'), variant: 'destructive' });
                    return;
                  }
                  setGpxSignedId(signedIds[0]);
                  setRemoveGpx(false);
                }}
                render={({ handleUpload }: { handleUpload: (files: FileList) => void }) => (
                  <>
                    <input
                      ref={gpxInputRef}
                      type="file"
                      accept=".gpx,application/gpx+xml"
                      className="sr-only"
                      onChange={(e) => {
                        const files = e.target.files;
                        if (!files || files.length === 0) return;
                        setGpxUploading(true);
                        setGpxName(files[0].name);
                        handleUpload(files);
                        e.target.value = '';
                      }}
                    />
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      disabled={gpxUploading}
                      onClick={() => gpxInputRef.current?.click()}
                      className="gap-2"
                    >
                      {gpxUploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
                      {t('cragAdmin.info.uploadGpx')}
                    </Button>
                  </>
                )}
              />
              {currentGpxName && (
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="gap-2 text-destructive"
                  onClick={() => {
                    setGpxSignedId(null);
                    setGpxName(null);
                    setRemoveGpx(true);
                  }}
                >
                  <Trash2 className="h-4 w-4" />
                  {t('cragAdmin.info.removeGpx')}
                </Button>
              )}
            </div>
          </div>

          <Button onClick={handleSave} disabled={saving || gpxUploading}>
            {saving ? t('gymAdmin.saving') : t('gymAdmin.save')}
          </Button>
        </CardContent>
      </Card>
    </div>
  );
}
